import { useCallback, useMemo } from 'react';
import {
  buildClinicVisitNoteDefault,
  parseVisitNotePreference,
  resolveVisitNoteConfig,
  qNoOp,
  VISIT_NOTE_PREFERENCE_KEY,
  type ClinicClassification,
  type GuidedNotePreset,
  type VisitNotePreference,
  type VisitNoteTabPref,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { useUserUiPreference } from '../../../hooks/useUserUiPreference';

// ─── Types ───

export interface TabConfig {
  key: string;
  label: string;
  visible: boolean;
}

export type LayoutStyle = 'tabs' | 'accordion' | 'guided_note';
export type AccordionMode = 'single' | 'multiple';
export type { GuidedNotePreset };

export interface LayoutConfig {
  style: LayoutStyle;
  accordionMode: AccordionMode;
  defaultOpenSection: string | null; // tab key
  rememberLastOpen: boolean;
  guidedNotePreset: GuidedNotePreset;
}

/**
 * PRD.300 — the shape the settings drawer edits. It is the persisted user
 * override payload (`user_ui_preferences.value_json`, key `visit_note`) plus
 * legacy field names kept so the drawer didn't need rewriting.
 */
export interface EncounterTabSettings {
  version: 1;
  /** Role preset id — front_desk | doctor | nurse | billing — or null. */
  preset: string | null;
  /** Sub-module visibility for the Tabs / Accordion layouts. */
  tabs: TabConfig[];
  layout?: LayoutConfig;
  /**
   * Per-guided-preset sub-module visibility. In Guided layout each discipline
   * keeps its OWN on/off set, so toggling under one preset never bleeds into
   * the others or into the shared Tabs/Accordion `tabs`.
   */
  guidedTabsByPreset?: Partial<Record<GuidedNotePreset, TabConfig[]>>;
}

/** Legacy per-user row (PRD.181). Read-only now — see `useEncounterTabConfig`. */
interface LegacyEncounterTabSettingsRow {
  id: string;
  org_id: string;
  user_id: string;
  preset: string | null;
  tabs_json: string;
  created_at: string;
  updated_at: string;
}

// ─── Default tabs ───

export const DEFAULT_TABS: TabConfig[] = [
  { key: 'summary', label: 'Summary', visible: true },
  // PRD.233 — Chief Complaint promoted to a first-class section sitting
  // immediately after Summary. Preference parsing will insert this for
  // existing users whose stored config predates this entry.
  { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
  { key: 'vitals', label: 'Vitals', visible: true },
  { key: 'charting', label: 'Charting', visible: true },
  { key: 'notes', label: 'Notes', visible: true },
  // PRD.214 — Symptoms & Diagnosis structured chip entry
  { key: 'symptoms', label: 'Symptoms', visible: true },
  { key: 'diagnoses', label: 'Diagnosis', visible: true },
  { key: 'prescriptions', label: 'Prescriptions', visible: true },
  { key: 'treatment_plan', label: 'Treatment Plan', visible: true },
  { key: 'forms', label: 'Forms', visible: true },
  { key: 'files', label: 'Files', visible: true },
  { key: 'invoices', label: 'Invoices', visible: true },
  // PRD.201 — Encounter Pharmacy tab. Sits between Invoices and Payments
  // because dispense eligibility is driven by paid invoice lines.
  { key: 'pharmacy', label: 'Pharmacy', visible: true },
  { key: 'payments', label: 'Payments', visible: true },
];

/**
 * Sub-modules kept in the codebase but suppressed everywhere in the UI — the
 * tab strip, the accordion, the guided cards AND the settings drawer's toggle
 * list (they flow through the same availability set as clinic/RBAC gating).
 *
 * This is a deliberate temporary hide, not a deletion: the catalogue entry, the
 * preset entries and the section renderer all stay, and saved preferences keep
 * their stored visibility for these keys. Removing a key from this set restores
 * it exactly as it was.
 */
export const HIDDEN_SUBMODULE_KEYS: ReadonlySet<string> = new Set([
  // Treatment Plan — hidden pending its rework. `TreatmentPlanSection` and the
  // `treatment_plan` cards in every guided preset remain wired up.
  'treatment_plan',
]);

/**
 * PRD.300 — layout defaults for callers that need a literal (the drawer's local
 * form state). The DERIVED clinic default comes from `clinicDefaultLayout`, not
 * from here; this is only the non-clinic-aware skeleton.
 */
export const DEFAULT_LAYOUT: LayoutConfig = {
  style: 'guided_note',
  accordionMode: 'single',
  defaultOpenSection: 'summary',
  rememberLastOpen: false,
  guidedNotePreset: 'general',
};

// ─── Presets ───

export const PRESETS: Record<string, { label: string; tabs: TabConfig[] }> = {
  front_desk: {
    label: 'Front Desk',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      // PRD.233 — Front desk may capture the patient's stated complaint
      // at intake, so visible by default.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
      { key: 'vitals', label: 'Vitals', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      { key: 'files', label: 'Files', visible: true },
      { key: 'invoices', label: 'Invoices', visible: true },
      { key: 'pharmacy', label: 'Pharmacy', visible: true },
      { key: 'payments', label: 'Payments', visible: true },
      { key: 'notes', label: 'Notes', visible: true },
      { key: 'charting', label: 'Charting', visible: false },
      { key: 'prescriptions', label: 'Prescriptions', visible: false },
      { key: 'treatment_plan', label: 'Treatment Plan', visible: false },
    ],
  },
  doctor: {
    label: 'Doctor',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      // PRD.233 — Chief Complaint is core clinical content for doctors.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
      { key: 'vitals', label: 'Vitals', visible: true },
      { key: 'charting', label: 'Charting', visible: true },
      { key: 'files', label: 'Files', visible: true },
      { key: 'notes', label: 'Notes', visible: true },
      { key: 'symptoms', label: 'Symptoms', visible: true }, // PRD.214
      { key: 'diagnoses', label: 'Diagnosis', visible: true }, // PRD.214
      { key: 'treatment_plan', label: 'Treatment Plan', visible: true },
      { key: 'prescriptions', label: 'Prescriptions', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      { key: 'pharmacy', label: 'Pharmacy', visible: true },
      { key: 'invoices', label: 'Invoices', visible: false },
      { key: 'payments', label: 'Payments', visible: false },
    ],
  },
  nurse: {
    label: 'Nurse',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      // PRD.233 — Nurses commonly capture / triage the complaint.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: true },
      { key: 'vitals', label: 'Vitals', visible: true },
      { key: 'notes', label: 'Notes', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      { key: 'files', label: 'Files', visible: true },
      { key: 'charting', label: 'Charting', visible: false },
      { key: 'prescriptions', label: 'Prescriptions', visible: false },
      { key: 'treatment_plan', label: 'Treatment Plan', visible: false },
      { key: 'invoices', label: 'Invoices', visible: false },
      { key: 'pharmacy', label: 'Pharmacy', visible: false },
      { key: 'payments', label: 'Payments', visible: false },
    ],
  },
  billing: {
    label: 'Billing',
    tabs: [
      { key: 'summary', label: 'Summary', visible: true },
      { key: 'invoices', label: 'Invoices', visible: true },
      { key: 'pharmacy', label: 'Pharmacy', visible: true },
      { key: 'payments', label: 'Payments', visible: true },
      { key: 'forms', label: 'Forms', visible: true },
      // PRD.233 — Billing role doesn't author chief complaint; hidden by default.
      { key: 'chief_complaint', label: 'Chief Complaint', visible: false },
      { key: 'files', label: 'Files', visible: false },
      { key: 'charting', label: 'Charting', visible: false },
      { key: 'notes', label: 'Notes', visible: false },
      { key: 'prescriptions', label: 'Prescriptions', visible: false },
      { key: 'treatment_plan', label: 'Treatment Plan', visible: false },
      { key: 'vitals', label: 'Vitals', visible: false },
    ],
  },
};

// ─── Hook ───

/**
 * PRD.300 — resolve the effective Visit Note configuration:
 *
 *   clinic default (from Clinic Type) → user override → availability
 *
 * The clinic default is derived on every read and never persisted, so a clinic
 * that changes its type immediately re-defaults every user who has not saved an
 * override. `resetToDefault` DELETES the override row rather than writing a
 * frozen copy of the current default.
 *
 * Availability (clinic module config, feature flags, RBAC) is applied by the
 * CALLER via `availableKeys`, and is authoritative: it can only subtract.
 */
export function useEncounterTabConfig(
  orgId: string,
  userId: string,
  classification: ClinicClassification,
  /**
   * Sub-module keys the clinic config + feature flags + RBAC currently allow.
   * `null` means "not resolved yet" — no constraint is applied so the workspace
   * never blanks mid-load.
   */
  availableKeys: Set<string> | null = null,
) {
  const {
    raw: overrideRaw,
    loaded: overrideLoaded,
    save,
    clear,
  } = useUserUiPreference(orgId, userId, VISIT_NOTE_PREFERENCE_KEY);

  // Legacy per-user row (PRD.181). Read-only: it only ever held the
  // Tabs/Accordion sub-module set, so it seeds `tabs` for users who saved
  // before PRD.300 and is superseded the moment they save again.
  const legacyReady = !!orgId && !!userId && overrideLoaded && overrideRaw === null;
  const { data: legacyRows = [] } = usePowerSyncQuery<LegacyEncounterTabSettingsRow>(
    legacyReady
      ? {
          key: ['encounter_tab_settings', orgId, userId],
          sql: `SELECT * FROM encounter_tab_settings WHERE org_id = ? AND user_id = ? LIMIT 1`,
          params: [orgId, userId],
          watch: ['encounter_tab_settings'],
        }
      : qNoOp('legacy-encounter-tab-settings'),
  );

  // ── 1. Clinic-derived default (never stored) ──
  const clinicDefault = useMemo(
    () => buildClinicVisitNoteDefault(classification, DEFAULT_TABS as VisitNoteTabPref[]),
    [classification],
  );

  // ── 2. User override, if any ──
  const override = useMemo<VisitNotePreference | null>(() => {
    if (overrideRaw !== null) {
      return parseVisitNotePreference(overrideRaw, clinicDefault, DEFAULT_TABS);
    }
    // Legacy fallback: a pre-PRD.300 row is a real user choice about
    // sub-modules, so honour it — but only for `tabs`. Layout style was never
    // in that table (it lived in localStorage), so it keeps the clinic default.
    const legacy = legacyRows[0];
    if (!legacy) return null;
    try {
      return parseVisitNotePreference(
        { tabs: JSON.parse(legacy.tabs_json), rolePreset: legacy.preset },
        clinicDefault,
        DEFAULT_TABS,
      );
    } catch {
      return null;
    }
  }, [overrideRaw, legacyRows, clinicDefault]);

  // ── 3 & 4. Merge, then intersect with availability ──
  const resolved = useMemo(
    () =>
      resolveVisitNoteConfig({
        clinicDefault,
        override,
        availability: { allowedKeys: availableKeys },
      }),
    [clinicDefault, override, availableKeys],
  );

  /** The settings the drawer edits — the override if saved, else the default. */
  const settings = useMemo<EncounterTabSettings>(() => {
    const source = override ?? clinicDefault;
    return {
      version: 1,
      preset: source.rolePreset,
      tabs: source.tabs,
      layout: source.layout,
      guidedTabsByPreset: source.guidedTabsByPreset,
    };
  }, [override, clinicDefault]);

  const updateSettings = useCallback(
    async (next: EncounterTabSettings) => {
      const payload: VisitNotePreference = {
        version: 1,
        layout: next.layout ?? clinicDefault.layout,
        rolePreset: next.preset,
        tabs: next.tabs,
        guidedTabsByPreset: next.guidedTabsByPreset ?? {},
      };
      await save(payload);
    },
    [save, clinicDefault.layout],
  );

  const applyPreset = useCallback(
    (presetKey: string) => {
      const preset = PRESETS[presetKey];
      if (!preset) return;
      updateSettings({
        version: 1,
        preset: presetKey,
        tabs: preset.tabs,
        layout: settings.layout,
        guidedTabsByPreset: settings.guidedTabsByPreset,
      });
    },
    [updateSettings, settings.layout, settings.guidedTabsByPreset],
  );

  /**
   * PRD §5.8 — remove the override so the user follows the LIVE clinic default
   * again. Deliberately not a write of the current default: a later change to
   * the clinic type must reach users who are on default.
   */
  const resetToDefault = useCallback(async () => {
    await clear();
  }, [clear]);

  return {
    settings,
    /** Visible sub-modules for Tabs / Accordion, already availability-filtered. */
    visibleTabs: resolved.visibleTabs as TabConfig[],
    /** Visible sub-modules for the active Guided preset, availability-filtered. */
    visibleGuidedTabs: resolved.visibleGuidedTabs as TabConfig[],
    layoutConfig: resolved.layout as LayoutConfig,
    /** True when the user has no saved override (following the clinic default). */
    isClinicDefault: resolved.isDefault,
    updateSettings,
    applyPreset,
    resetToDefault,
  };
}
